/** `snippets/product-slideshow.liquid` — `<product-slideshow>`: product gallery
 *  with a Swiper main slideshow that crossfades between slides on a timer plus
 *  a thumbnail row that always shows at most TWO thumbnails — the "next" media
 *  items in the cycle, excluding whichever slide is currently active. Each
 *  thumb slot uses two stacked layers (`a`/`b`) so its contents can crossfade
 *  whenever the active slide changes. Clicking a thumb jumps the main
 *  slideshow to whichever media that slot is currently showing.
 *
 *  Video handling: gallery videos are muted, controless, and loop. The active
 *  slide's video is rewound and played; all other videos are paused/reset on
 *  every slide change so duplicates inserted by Swiper's loop mode never play
 *  in the background. The autoplay timer drives advancement for every slide
 *  type equally, so a looping video keeps playing until the timer moves on.
 */

import Swiper from 'swiper'
import { Autoplay, EffectFade } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-fade'

const MAIN_SELECTOR = '[data-product-slideshow-main]'
const SLIDE_SELECTOR = '.swiper-slide'
const THUMB_SLOT_SELECTOR = '[data-product-slideshow-thumb-slot]'
const THUMB_TEMPLATE_SELECTOR = '[data-product-slideshow-thumb-template]'
const LAYER_SELECTOR = '[data-layer]'
const LAYER_FRONT_CLASS = 'product-slideshow-thumb-layer-front'
const AUTOPLAY_DELAY_MS = 5000
const FADE_DURATION_MS = 500

class ProductSlideshow extends HTMLElement {
  #abort
  #swiper
  #thumbSlots = []
  #thumbTemplates = []
  #mediaCount = 0

  connectedCallback() {
    const mainEl = this.querySelector(MAIN_SELECTOR)
    if (!mainEl) return

    this.#thumbSlots = Array.from(this.querySelectorAll(THUMB_SLOT_SELECTOR))
    this.#thumbTemplates = Array.from(this.querySelectorAll(THUMB_TEMPLATE_SELECTOR))
    const originalSlides = Array.from(this.querySelectorAll(`${MAIN_SELECTOR} ${SLIDE_SELECTOR}`))
    this.#mediaCount = this.#thumbTemplates.length || originalSlides.length

    this.#abort = new AbortController()
    const { signal } = this.#abort

    const slideCount = originalSlides.length

    // Autoplay delay (ms) is set per section via `data-slideshow-timeout`; fall
    // back to the default when the attribute is missing or invalid.
    const autoplayDelay =
      Number.parseInt(this.dataset.slideshowTimeout ?? '', 10) || AUTOPLAY_DELAY_MS

    this.#swiper = new Swiper(mainEl, {
      modules: [Autoplay, EffectFade],
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slideCount > 1,
      speed: FADE_DURATION_MS,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      allowTouchMove: slideCount > 1,
      autoplay:
        slideCount > 1
          ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
      on: {
        slideChange: (swiper) => this.#renderThumbs(swiper.realIndex, true),
        slideChangeTransitionStart: () => this.#stopAllVideos(),
        slideChangeTransitionEnd: (swiper) => this.#handleActiveMedia(swiper),
      },
    })

    this.#thumbSlots.forEach((slot) => {
      slot.addEventListener('click', this.#onThumbClick, { signal })
    })

    this.#renderThumbs(this.#swiper?.realIndex ?? 0, false)
    // Kick off media handling for the initially-active slide (no transition fires on init).
    this.#handleActiveMedia(this.#swiper)
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#swiper?.destroy(true, true)
    this.#swiper = undefined
  }

  #onThumbClick = (event) => {
    const slot = event.currentTarget
    const index = Number.parseInt(slot.dataset.currentIndex ?? '', 10)
    if (Number.isNaN(index) || !this.#swiper) return
    this.#swiper.slideToLoop(index)
  }

  /** Indices for the up-to-two visible thumb slots, cycling and excluding active. */
  #computeNextIndices(activeIndex) {
    const indices = []
    if (this.#mediaCount <= 1) return indices
    for (let offset = 1; indices.length < this.#thumbSlots.length && offset < this.#mediaCount; offset += 1) {
      indices.push((activeIndex + offset) % this.#mediaCount)
    }
    return indices
  }

  #renderThumbs(activeIndex, animate) {
    if (this.#thumbSlots.length === 0) return
    const nextIndices = this.#computeNextIndices(activeIndex)

    this.#thumbSlots.forEach((slot, slotIndex) => {
      const mediaIndex = nextIndices[slotIndex]
      if (mediaIndex == null) {
        this.#clearSlot(slot)
        return
      }

      // Already showing this media — nothing to crossfade.
      if (Number.parseInt(slot.dataset.currentIndex ?? '', 10) === mediaIndex) return

      const layers = slot.querySelectorAll(LAYER_SELECTOR)
      if (layers.length < 2) return

      const front = slot.querySelector(`.${LAYER_FRONT_CLASS}`) ?? layers[0]
      const back = front === layers[0] ? layers[1] : layers[0]

      this.#fillLayer(back, mediaIndex)
      slot.dataset.currentIndex = String(mediaIndex)
      slot.disabled = false
      slot.setAttribute(
        'aria-label',
        `Show image ${mediaIndex + 1} of ${this.#mediaCount}`,
      )

      if (!animate) {
        front.classList.remove(LAYER_FRONT_CLASS)
        back.classList.add(LAYER_FRONT_CLASS)
        return
      }

      // Force a paint with the new content already in `back` (still hidden),
      // then swap which layer is the front so CSS opacity transitions crossfade.
      requestAnimationFrame(() => {
        back.classList.add(LAYER_FRONT_CLASS)
        front.classList.remove(LAYER_FRONT_CLASS)
      })
    })
  }

  #fillLayer(layer, mediaIndex) {
    const template = this.#thumbTemplates[mediaIndex]
    layer.replaceChildren()
    if (!template?.content) return
    layer.appendChild(template.content.cloneNode(true))

    // Video thumbs loop on their own, but cloned `<video autoplay>` nodes don't
    // always start on their own — nudge muted playback so the thumb animates.
    const video = layer.querySelector('video')
    if (video) {
      video.muted = true
      const playPromise = video.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    }
  }

  #clearSlot(slot) {
    slot.querySelectorAll(LAYER_SELECTOR).forEach((layer) => {
      layer.replaceChildren()
      layer.classList.remove(LAYER_FRONT_CLASS)
    })
    delete slot.dataset.currentIndex
    slot.disabled = true
  }

  /** Pause + rewind every video in the gallery (covers Swiper-duplicated slides too). */
  #stopAllVideos() {
    this.querySelectorAll(`${MAIN_SELECTOR} video`).forEach((video) => {
      try {
        video.pause()
        video.currentTime = 0
      } catch {
        // Some browsers throw if metadata isn't loaded yet — safe to ignore.
      }
    })
  }

  /**
   * Play the active slide's looping video (if any). The autoplay timer keeps
   * running for every slide type, so a video simply loops until the timer
   * advances to the next slide.
   */
  #handleActiveMedia(swiper) {
    if (!swiper) return

    // Autoplay always drives advancement now (videos loop in the background).
    swiper.autoplay?.start()

    const activeSlide = swiper.slides?.[swiper.activeIndex]
    const video = activeSlide?.querySelector('video')
    if (!video) return

    try {
      video.currentTime = 0
    } catch {
      // Ignore — may not be seekable yet.
    }
    const playPromise = video.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      // Browser blocked playback — the poster/first frame stays visible.
      playPromise.catch(() => {})
    }
  }
}

if (!customElements.get('product-slideshow')) {
  customElements.define('product-slideshow', ProductSlideshow)
}
